/* eslint-disable */
import { UntypedFormGroup } from '@angular/forms';
import { environment } from 'src/environments/environment';
import { Observable, Subject } from 'rxjs';
import { FlagChangedValue } from '../models/interface/flagChangedValue.interface';

/**
 * Private Class use only in flags
 */
class FlagValue {
  /**
   * this field increase when the flag is used
   */
  private versionValue: number;
  /**
   * the value of the flag
   */
  private stateValue: boolean;

  /**
   * subject to register changes in flag
   */
  private subject: Subject<FlagChangedValue>;

  /**
   * Observable to see changes
   */
  private observable$: Observable<FlagChangedValue>;

  constructor() {
    this.versionValue = 0;
    this.stateValue = false;
  }

  /**
   * get the version of the flag
   */
  get version(): number {
    return this.versionValue;
  }

  /**
   * get state of the flag
   */
  get state(): boolean {
    return this.stateValue;
  }

  /**
   * update state value
   */
  set state(value: boolean) {
    this.stateValue = value;
    if (this.subject) {
      this.subject.next({
        value,
        toggle: () => {
          this.stateValue = !value;
        },
      });
    }
  }

  /**
   * Update the version and state then return a closure to validate Flag version
   */
  take(): FlagNotChanged {
    this.state = true;
    this.versionValue += 1;
    const momentVersion = this.versionValue;
    return () => {
      return momentVersion === this.versionValue;
    };
  }

  get valueChanges(): Observable<FlagChangedValue> {
    if (this.observable$) {
      return this.observable$;
    }
    this.subject = new Subject();
    this.observable$ = this.subject.asObservable();
    return this.observable$;
  }
}

export type FlagNotChanged = () => boolean;

export class Flags<T> {
  /**
   * model with filter values
   */
  private flagsData = new Map<T, FlagValue>();
  /**
   * form group to validate action
   */
  private formGroup: UntypedFormGroup;
  bindingFlags: T[];
  checkDirtyState: boolean;

  constructor() {
    /** */
  }

  /**
   * Check if the flag value
   * @param  {T} key flag's name to get the value
   * @returns boolean
   */
  get(key: T): boolean {
    const value = this.flagsData.get(key);
    if (value == null) {
      return false;
    }
    return value.state;
  }

  /**
   * returns observable from flags value changes
   *
   * @param  {T} key
   * @returns Observable
   */
  valueChanges(key: T): Observable<FlagChangedValue> {
    return this.getFlagValue(key).valueChanges;
  }

  /**
   * get the flag value
   * @param  {T} key flag's name to get the value
   * @returns FlagValue the flag
   */
  private getFlagValue(key: T): FlagValue {
    let value = this.flagsData.get(key);
    if (value == null) {
      value = new FlagValue();
      this.flagsData.set(key, value);
    }
    return value;
  }

  /**
   * enable a flag, then execute a code and disable the flag at the end
   * @param  {string} name Flag name
   * @param  {(FlagNotChanged)=>Promise<T>} code code to execute
   * @returns Promise
   */
  async execute<U>(key: T, code: (flagUsed: FlagNotChanged) => Promise<U>, bind: boolean = true): Promise<U> {
    this.validateBinding(key, bind);
    const flag = this.getFlagValue(key);
    const test = flag.take();
    try {
      const result = await this.delayPromise(async () => {
        return code(test);
      });
      return result;
    } finally {
      if (test()) {
        flag.state = false;
      }
    }
  }

  /**
   * Execute a callback after certain delay and then resolve a promise with its value
   * @param callback
   * @param delay default delay is 100ms
   */
  delayPromise<Z>(callback: () => Z, delay: number = 100): Promise<Z> {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        try {
          resolve(callback());
        } catch (e) {
          reject(e);
        }
      }, 100);
    });
  }

  /**
   * enable a flag, then execute a code
   * @param  {string} name Flag name
   * @param  {(FlagNotChanged)=>Promise<T>} code code to execute
   * @returns Promise
   */
  async enable<U>(key: T, code?: (flagUsed: FlagNotChanged) => Promise<U>, bind: boolean = true): Promise<U> {
    this.validateBinding(key, bind);
    const flag = this.getFlagValue(key);
    if (flag.state) {
      return environment.guardClause;
    }
    const test = flag.take();
    const result = await this.delayPromise(async () => {
      if (!code) {
        return environment.guardClause;
      }
      return code(test);
    });
    return result;
  }

  /**
   * enable a flag, then execute a code
   * @param  {string} name Flag name
   * @returns Promise
   */
  disable(key: T): void {
    const flag = this.getFlagValue(key);
    if (!flag.state) {
      return environment.guardClause;
    }
    flag.state = false;
  }

  /**
   * enable a flag, then execute a code and disable the flag at the end but only if it is False
   * @param  {string} name Flag name
   * @param  {(FlagNotChanged)=>Promise<T>} code code to execute
   * @returns Promise
   */
  async executeIfFalse<U>(key: T, code: (flagUsed: FlagNotChanged) => Promise<U>, validateBinding = true): Promise<U | null> {
    this.validateBinding(key, validateBinding);
    const flag = this.getFlagValue(key);
    if (flag.state) {
      return environment.guardClause;
    }
    const test = flag.take();
    try {
      const result = await this.delayPromise(async () => {
        return code(test);
      });
      return result;
    } finally {
      if (test()) {
        flag.state = false;
      }
    }
  }

  /**
   * bind form group variable
   * @param formGroup
   */
  bindFromGroup(formGroup: UntypedFormGroup, checkDirtyState: boolean = true, ...flags: T[]): void {
    this.formGroup = formGroup;
    this.bindingFlags = flags;
    this.checkDirtyState = checkDirtyState;
  }

  /**
   * validate binding
   * @param bind
   */
  validateBinding(key: T, bind: boolean = true): void {
    if (!bind) {
      return environment.guardClause;
    }
    if (!this.formGroup) {
      return environment.guardClause;
    }
    const useBindingFlags = this.bindingFlags && this.bindingFlags.length > 0;
    if (useBindingFlags && !this.bindingFlags.includes(key)) {
      return environment.guardClause;
    }
    const invalid = this.formGroup.invalid;
    const dirty = this.checkDirtyState ? this.formGroup.dirty : true;
    if (invalid || !dirty) {
      throw new Error('FLAG_BINDING');
    }
  }

  /**
   * toggle a flag
   * @param  {string} name Flag name
   * @param  {(FlagNotChanged)=>Promise<T>} code code to execute
   * @returns Promise with previous value
   */
  async toggle<U>(key: T): Promise<boolean> {
    const value = this.get(key);
    if (value) {
      this.disable(key);
      return true;
    }
    await this.enable(key);
    return false;
  }
}
